import { GetServerSidePropsContext } from "next";

import PageErrorBoundary from "shared/providers/PageErrorBoundary";
import { setCookiesPromoterLink } from "shared/utils/affiliates/setCookiesPromoterLink";
import { setPromoterClick } from "shared/utils/affiliates/setPromoterClicks";
import { makeCustomNotFoundPath } from "shared/utils/customNotFound";
import { getPlatformOrgIdFromCookie } from "shared/utils/getPlatformOrgIdFromCookie";
import { saveNotFoundURLInCookies } from "shared/utils/saveNotFoundURLInCookies";

import { IGroup } from "../../@types";
import checkIfUserHasAccessToGroup from "../../utils/checkIfUserHasAccessToGroup";
import { getGroup } from "../../utils/fetchGroupSlug";
import ProductPresentationTemplate from "./components/templates/ProductPresentationTemplate";
import { PagePresentationProvider } from "./contexts/PagePresentationContext";
import { ProductPresentationPageContainer } from "./styles";

interface IProductPresentationPage {
  groupData: IGroup;
}
const ProductPresentationPage = ({ groupData }: IProductPresentationPage) => {
  return (
    <PageErrorBoundary>
      <PagePresentationProvider>
        <ProductPresentationPageContainer>
          <ProductPresentationTemplate groupData={groupData} />
        </ProductPresentationPageContainer>
      </PagePresentationProvider>
    </PageErrorBoundary>
  );
};

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  const { token: access_token } = ctx.req.cookies;

  const organizationId = getPlatformOrgIdFromCookie(ctx);

  const token = access_token ? JSON?.parse(access_token) : null;
  const group_slug = ctx?.params?.group_slug as string;

  const headers = {
    Authorization: `Bearer ${token}`,
    "X-Requested-With": "XMLHttpRequest",
  };

  let hasAccess = false;
  let groupData: IGroup;

  if (organizationId !== null) {
    groupData = await getGroup({
      group_slug,
      headers,
      organizationId,
    });

    if (token !== null) {
      hasAccess = await checkIfUserHasAccessToGroup({
        group: groupData,
        headers,
        isMember: groupData?.member_info?.is_member,
        organizationId,
      });
    }
  }

  if (hasAccess) {
    return {
      redirect: {
        permanent: false,
        destination: `/g/${group_slug}`,
      },
    };
  }

  if (!groupData?.id) {
    const currentUrlThatIsNotFounded = ctx?.req?.url;
    saveNotFoundURLInCookies(currentUrlThatIsNotFounded, ctx);

    return {
      redirect: {
        permanent: false,
        destination: makeCustomNotFoundPath({
          itemSlug: group_slug,
          page: "group",
        }),
      },
    };
  }

  const ref = ctx?.query?.ref as string;
  if (!!ref && !!groupData?.promoter_active) {
    setCookiesPromoterLink(
      ctx,
      ref,
      group_slug,
      groupData?.promoter_commission,
    );

    setPromoterClick({
      group_id: groupData?.id,
      promoter_link: ref,
      user_id: groupData?.user_id,
    });
  }

  return {
    props: { groupData },
  };
};

export default ProductPresentationPage;
